import { useEffect, useState, useRef } from "react";
import useApiToken from "../../hooks/useApiToken";
import { Popover } from "@headlessui/react";
import {
  CandidateStatus,
  RoleStatus,
  CandidateManualEvaluation,
  Candidate,
} from "../../Models";
import {
  StarIcon,
  DocumentChartBarIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  CheckIcon,
  BookOpenIcon,
} from "@heroicons/react/24/solid";
import CandidateAssessmentResult from "./CandidateAssessmentResults";
import ConfirmModal from "../Shared/ConfirmModal";
import { GetStatusForRole } from "../../api/roles.api";
import { GetCandidateManualEvaluations, GetCandidateScore, GetCandidateStatus, ResetCandidateScore, SubmitManualEvaluation } from "../../api/candidates.api";
import { getTakenWithoutSebFlags } from "../../api/assessmentslots.api";
import { useNotification } from "../../context/NotificationContext";
import RequiredScorecardModal from "./InterviewEvaluation/RequiredScorecardModal";
import StructuredEvaluationDetailModal from "./InterviewEvaluation/StructuredEvaluationDetailModal";
import ScoreFormulaInfo from "./InterviewEvaluation/ScoreFormulaInfo";
import InterviewGuideModal from "./InterviewEvaluation/InterviewGuideModal";
import useInterviewGuide from "./InterviewEvaluation/useInterviewGuide";
import {
  GetStageEvaluations,
  RECOMMENDATION_LABELS,
  RECOMMENDATION_STYLES,
  StageEvaluationSummary,
} from "../../api/interviewEvaluation.api";

const getRatingGrade = (rating: string | undefined) => {
  if (!rating) return "";
  return rating === "Excellent"
    ? "E"
    : rating === "Pass"
      ? "P"
      : rating === "Fail"
        ? "F"
        : rating === "Waitlist"
          ? "W"
          : "";
};

type props = {
  candidateId?: string;
  roleId?: string;
  hideScores?: boolean;
  status?: string;
  candidate?: Candidate;
  onGradeUpdate?: (
    candidatesUpdate: CandidateStatus[],
    gradeStatusUpdate?: boolean,
    manualEvaluation?: { roleStatusId: string; rating: string; score?: number }
  ) => void;
};

export default function ScoreGrades({
  candidateId,
  roleId,
  hideScores,
  status,
  candidate,
  onGradeUpdate,
}: props) {
  const [grades, setGrades] = useState<string[]>([]);
  const [roleStatuses, setRoleStatuses] = useState<RoleStatus[]>([]);
  const [candidateStatuses, setCandidateStatuses] = useState<CandidateStatus[]>(
    []
  );
  const [showCandidateResult, setShowCandidateResult] = useState(false);
  const [scores, setScores] = useState<
    {
      score: number;
      roleStatus: RoleStatus;
      roleAssessmentMappingId?: string;
    }[]
  >([]);
  const [manualEvaluations, setManualEvaluations] = useState<
    CandidateManualEvaluation[]
  >([]);
  const [manualScores, setManualScores] = useState<{ [key: string]: string }>(
    {}
  );
  const [current, setCurrent] = useState<RoleStatus | undefined>();
  const [resettingScoreId, setResettingScoreId] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [takenWithoutSebFlags, setTakenWithoutSebFlags] = useState<Record<string, boolean>>({});
  const [modalData, setModalData] = useState<{
    roleAssessmentMappingId: string;
    statusName: string;
  } | null>(null);
  const [editingScoreValues, setEditingScoreValues] = useState<{
    [key: string]: string;
  }>({});
  const token = useApiToken();
  const { error: notifyError } = useNotification();
  const interviewGuide = useInterviewGuide();
  // Drives RequiredScorecardModal. `rating` present = a grade click triggered this,
  // either because the backend rejected it (no structured evaluation exists yet for
  // this non-final stage - allowSkip stays false) or because it's the very first grade
  // on a final-interview stage (allowSkip true, offered rather than required). `rating`
  // absent = opened voluntarily via the "Scorecard" button, independent of any grade.
  const [scorecardModal, setScorecardModal] = useState<{
    roleStatusId: string;
    stageName: string;
    rating?: string;
    allowSkip?: boolean;
    // Re-issues the originally-clicked grade so it wins over whatever
    // ManualEvaluationProjectionService derived from the scorecard's competency
    // scores - fired both on Skip (bypassing the scorecard) and after a normal
    // submit (overriding the auto-projected rating), so the click is always the
    // final word.
    applyPendingGrade?: () => void;
  } | null>(null);
  const [structuredSummaries, setStructuredSummaries] = useState<
    Record<string, StageEvaluationSummary>
  >({});
  const [viewingStructured, setViewingStructured] = useState<{
    roleStatusId: string;
    stageName: string;
  } | null>(null);

  useEffect(() => {
    if (!status) return;
    const curr = roleStatuses.find(
      (rs) => rs.name.toLowerCase() === status.toLowerCase()
    );
    setCurrent(curr);
  }, [status, roleStatuses]);

  // Extracted so it can be re-run on demand - specifically, right after a structured
  // evaluation is submitted from RequiredScorecardModal, so the score box updates live
  // instead of requiring a page reload to see what the submission just projected into
  // CandidateManualEvaluation.
  const refreshManualGrades = () => {
    if (!roleId || !token || !candidateId) return;

    GetCandidateScore(candidateId, token, roleId).then((result: any) => {
      if (result) {
        setScores(result.filter((sco: any) => sco.score !== null));
      }
      GetCandidateManualEvaluations(candidateId, token, roleId).then((result: any) => {
        if (result) {
          setManualEvaluations(result);
          const scoreMap: { [key: string]: string } = {};
          result.forEach((evaluation: CandidateManualEvaluation) => {
            if (evaluation.score) {
              scoreMap[evaluation.roleStatusId] = evaluation.score.toString();
            }
          });
          setManualScores(scoreMap);
        }
      });
    });
  };

  // Same reasoning - re-run after a submission so the recommendation badge (and its
  // score) refreshes without a reload, alongside the manual grade it just produced.
  const refreshStructuredSummaries = () => {
    if (!token || !candidateId || !roleId || roleStatuses.length === 0) return;

    const gradingStages = roleStatuses.filter(
      (rs) =>
        rs.roleStatusType?.name === "Interview" ||
        rs.roleStatusType?.name === "Assignment"
    );

    Promise.all(
      gradingStages.map(async (rs) => {
        try {
          const summary = await GetStageEvaluations(candidateId, roleId, rs.id, token);
          return summary && summary.evaluations.length > 0
            ? ([rs.id, summary] as const)
            : null;
        } catch {
          return null;
        }
      })
    ).then((results) => {
      const map: Record<string, StageEvaluationSummary> = {};
      results.forEach((r) => {
        if (r) map[r[0]] = r[1];
      });
      setStructuredSummaries(map);
    });
  };

  useEffect(() => {
    if (!roleId || !token) return;
    GetStatusForRole(roleId, token).then((sr) => {
      setRoleStatuses(sr.roleStatuses.sort((a, b) => a.order - b.order));
    });
    if (!candidateId) return;

    refreshManualGrades();

    GetCandidateStatus(candidateId, token).then((cs) => {
      const filtered = cs.filter((c) => c.roleId === roleId);
      setCandidateStatuses(filtered);
    });

    // Fetch per-slot takenWithoutSeb flags keyed by roleStatusId.
    getTakenWithoutSebFlags(candidateId, token, roleId).then(setTakenWithoutSebFlags);
    // refreshManualGrades intentionally excluded - it closes over the same
    // token/roleId/candidateId already in this effect's own dependency list, so adding
    // it here would only make the dependency array circular for no behavioural gain.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, roleId, candidateId]);

  // Structured-evaluation badge shown next to the manual score for each stage. Fetched
  // once roleStatuses is known; silently yields nothing per-stage when the feature is
  // off or no structured evaluation exists there, so pre-existing candidates and
  // Assessment-type stages show no badge at all.
  useEffect(() => {
    refreshStructuredSummaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, candidateId, roleId, roleStatuses]);

  useEffect(() => {
    const candidateGrades = roleStatuses.slice(1, -2).map((rs) => {
      if (
        rs.roleStatusType?.name === "Interview" ||
        rs.roleStatusType?.name === "Assignment"
      ) {
        const manualEval = manualEvaluations.find(
          (e) => e.roleStatusId === rs.id
        );
        const grade = getRatingGrade(manualEval?.rating);
        return grade;
      }
      return "";
    });

    setGrades(candidateGrades);
  }, [roleStatuses, candidateStatuses, manualEvaluations]);

  // "Final interview" stages are graded manually by design (see
  // CandidatesController.CreateManualEvaluation) - the scorecard is offered there, not
  // required. Matched on the stage name for now, mirroring the backend's own check.
  const isFinalInterviewStage = (rs: RoleStatus) =>
    rs.name?.toLowerCase().includes("final") ?? false;

  // The actual grade-recording call, extracted purely so ScoreChange and GradeClick
  // don't duplicate the success/failure handling.
  const submitLegacyGrade = (
    index: number,
    clickedGrade: string,
    rating: "Excellent" | "Pass" | "Fail" | "Waitlist" | "",
    rs: RoleStatus,
    isTogglingOff: boolean
  ) => {
    if (!candidateId || !roleId || !token) return;

    const score = manualScores[rs.id]
      ? parseFloat(manualScores[rs.id])
      : undefined;

    SubmitManualEvaluation(
      {
        candidateId,
        roleId,
        roleStatusId: rs.id,
        rating,
        score,
      },
      token
    )
      .then((response) => {
        const newGrades = [...grades];
        newGrades[index] = isTogglingOff ? "" : clickedGrade;
        setGrades(newGrades);

        const existingEvalIndex = manualEvaluations.findIndex(
          (e) => e.roleStatusId === rs.id
        );
        const newEvaluation: CandidateManualEvaluation = {
          id:
            response.id ||
            (existingEvalIndex >= 0
              ? manualEvaluations[existingEvalIndex].id
              : ""),
          candidateId,
          roleId,
          roleStatusId: rs.id,
          rating,
          score,
          evaluatedAt: new Date(),
        };

        if (existingEvalIndex >= 0) {
          const updatedEvaluations = [...manualEvaluations];
          updatedEvaluations[existingEvalIndex] = newEvaluation;
          setManualEvaluations(updatedEvaluations);
        } else {
          setManualEvaluations([...manualEvaluations, newEvaluation]);
        }

        if (candidateId && roleId) {
          GetCandidateScore(candidateId, token, roleId).then((result: any) => {
            if (result.manualEvaluations) {
              setManualEvaluations(result.manualEvaluations);
              const scoreMap: { [key: string]: string } = {};
              result.manualEvaluations.forEach(
                (evaluation: CandidateManualEvaluation) => {
                  if (evaluation.score) {
                    scoreMap[evaluation.roleStatusId] = evaluation.score.toString();
                  }
                }
              );
              setManualScores(scoreMap);
            }
          });
        }

        if (onGradeUpdate && candidate) {
          const candidateStatus = candidate.candidateStatuses.find(
            (c: CandidateStatus) => c.roleStatusId === rs.id
          );
          if (candidateStatus) {
            onGradeUpdate([candidateStatus], true, {
              roleStatusId: rs.id,
              rating,
              score,
            });
          }
        }
      })
      .catch((error) => {
        console.error("Failed to update manual evaluation:", error);

        const message =
          (error as Error)?.message?.replace(/^"|"$/g, "") ||
          "Failed to update the grade.";

        // The backend blocks Pass/Excellent on (non-final) Interview stages until a
        // structured evaluation has been submitted. Open the scorecard right here
        // instead of just telling them to go find it - InterviewScorecard only needs
        // candidateId/roleId/roleStatusId, so it can be filled in without leaving this
        // page.
        if (message.toLowerCase().includes("structured evaluation must be submitted")) {
          setScorecardModal({
            roleStatusId: rs.id,
            stageName: rs.name,
            rating,
            applyPendingGrade: () =>
              submitLegacyGrade(index, clickedGrade, rating, rs, isTogglingOff),
          });
          return;
        }

        notifyError("Could not update grade", message);
      });
  };

  const handleGradeClick = (
    index: number,
    clickedGrade: string,
    rs: RoleStatus
  ) => {
    if (!candidateId || !roleId || !token) return;

    const candidateStatus = candidateStatuses.find(
      (c) => c.roleStatusId === rs.id
    );
    if (!candidateStatus) return;

    const currentGrade = grades[index];
    const isTogglingOff = currentGrade === clickedGrade;
    const rating = isTogglingOff
      ? ""
      : clickedGrade === "E"
        ? "Excellent"
        : clickedGrade === "P"
          ? "Pass"
          : clickedGrade === "F"
            ? "Fail"
            : clickedGrade === "W"
              ? "Waitlist"
              : "";

    if (
      rs.roleStatusType?.name === "Interview" ||
      rs.roleStatusType?.name === "Assignment"
    ) {
      if (rating !== "" && !rating) return;

      // Final-interview stages: offer the scorecard up front, but only the very first
      // time this candidate is graded on this stage - "currentGrade === ''" means no
      // rating has ever been set here yet, regardless of whether that eventually
      // happens via Skip or via the scorecard itself, so this never re-fires on a
      // later grade change. That's what makes this different from before: once any
      // grade exists, every subsequent click acts directly with no modal, same as
      // every other stage - the interruption is a one-time nudge, not a standing gate.
      if (
        (rating === "Excellent" || rating === "Pass") &&
        currentGrade === "" &&
        isFinalInterviewStage(rs)
      ) {
        setScorecardModal({
          roleStatusId: rs.id,
          stageName: rs.name,
          rating,
          allowSkip: true,
          applyPendingGrade: () =>
            submitLegacyGrade(index, clickedGrade, rating, rs, isTogglingOff),
        });
        return;
      }

      // Every other case acts directly and immediately: non-final stages that already
      // have a grade, final-interview stages past their first grade, Fail/Waitlist
      // clicks, and toggling a selected grade off. Non-final stages that have never had
      // a structured evaluation still fall back to the required modal below, via the
      // backend rejection - that gate is unchanged.
      submitLegacyGrade(index, clickedGrade, rating, rs, isTogglingOff);
    }
  };

  const handleScoreChange = (rs: RoleStatus, value: string) => {
    if (!candidateId || !roleId || !token) return;

    const numValue = value === "" ? undefined : parseFloat(value);
    if (numValue !== undefined && (numValue < 0 || numValue > 10)) return;

    setManualScores({ ...manualScores, [rs.id]: value });

    let currentEvaluation = manualEvaluations.find(
      (e) => e.roleStatusId === rs.id
    );

    SubmitManualEvaluation(
      {
        candidateId,
        roleId,
        roleStatusId: rs.id,
        // null, not "", when there's no cached evaluation yet - this is a score-only
        // edit and has no business clearing a grade it doesn't even know about. See
        // CandidatesController.CreateManualEvaluation for the matching backend side.
        rating: currentEvaluation ? currentEvaluation.rating : null,
        score: numValue,
      },
      token
    )
      .then((response) => {
        const evalIndex = manualEvaluations.findIndex(
          (e) => e.roleStatusId === rs.id
        );
        if (evalIndex >= 0) {
          const updatedEvaluations = [...manualEvaluations];
          updatedEvaluations[evalIndex] = {
            ...updatedEvaluations[evalIndex],
            score: numValue,
            evaluatedAt: new Date(),
          };
          setManualEvaluations(updatedEvaluations);

          if (candidateId && roleId) {
            GetCandidateScore(candidateId, token, roleId).then(
              (result: any) => {
                if (result.manualEvaluations) {
                  setManualEvaluations(result.manualEvaluations);
                  const scoreMap: { [key: string]: string } = {};
                  result.manualEvaluations.forEach(
                    (evaluation: CandidateManualEvaluation) => {
                      if (evaluation.score) {
                        scoreMap[evaluation.roleStatusId] =
                          evaluation.score.toString();
                      }
                    }
                  );
                  setManualScores(scoreMap);
                }
              }
            );
          }

          if (onGradeUpdate && candidate) {
            const candidateStatus = candidate.candidateStatuses.find(
              (c: CandidateStatus) => c.roleStatusId === rs.id
            );
            if (candidateStatus) {
              onGradeUpdate([candidateStatus], true, {
                roleStatusId: rs.id,
                rating: currentEvaluation ? currentEvaluation.rating : "",
                score: numValue,
              });
            }
          }
        }
      })
      .catch((error) => {
        console.error("Failed to update score:", error);
      });
  };

  const handleScoreInputChange = (rsId: string, value: string) => {
    if (value === "" || (parseFloat(value) >= 0 && parseFloat(value) <= 10)) {
      setEditingScoreValues({ ...editingScoreValues, [rsId]: value });
    }
  };

  const handleSaveScore = (rs: RoleStatus) => {
    if (!candidateId || !roleId || !token) return;

    const scoreValue = editingScoreValues[rs.id] ?? manualScores[rs.id] ?? "";
    const numValue = scoreValue === "" ? undefined : parseFloat(scoreValue);
    if (
      scoreValue !== "" &&
      (isNaN(numValue!) || numValue! < 0 || numValue! > 10)
    ) {
      return;
    }

    const currentEvaluation = manualEvaluations.find(
      (e) => e.roleStatusId === rs.id
    );

    // if (!currentEvaluation || !currentEvaluation.rating) {
    //   alert(
    //     "Please select a grade (Excellent, Pass, Fail, or Waitlist) before adding a score."
    //   );
    //   return;
    // }

    handleScoreChange(rs, scoreValue);
    const newEditingValues = { ...editingScoreValues };
    delete newEditingValues[rs.id];
    setEditingScoreValues(newEditingValues);
  };

  const candidateReachedStatus = (rs: RoleStatus) => {
    return candidateStatuses.some((c) => c.roleStatusId === rs.id);
  };

  const handleResetSpecificScore = async (
    roleAssessmentMappingId: string,
    statusName: string
  ) => {
    setModalData({ roleAssessmentMappingId, statusName });
    setShowConfirmModal(true);
  };

  const confirmResetScore = async () => {
    if (!candidateId || !token || !modalData) return;

    setResettingScoreId(modalData.roleAssessmentMappingId);
    setShowConfirmModal(false);

    try {
      await ResetCandidateScore(
        candidateId,
        token,
        modalData.roleAssessmentMappingId
      );
      const response = await GetCandidateScore(candidateId, token, roleId);
      const updatedScores: {
        score: number;
        roleStatus: RoleStatus;
        roleAssessmentMappingId?: string;
      }[] = Array.isArray(response) ? response : response.automatedScores || [];
      setScores(
        updatedScores.filter(
          (sco: {
            score: number;
            roleStatus: RoleStatus;
            roleAssessmentMappingId?: string;
          }) => sco.score !== null
        )
      );

      const updatedStatuses = await GetCandidateStatus(candidateId, token);
      setCandidateStatuses(updatedStatuses.filter((c) => c.roleId === roleId));

      setShowSuccessModal(true);
    } catch (error) {
      console.error("Error resetting specific score:", error);
      alert("Failed to reset score. Please try again.");
    } finally {
      setResettingScoreId(null);
      setModalData(null);
    }
  };

  const gradeColors = {
    E: {
      bg: "bg-emerald-500",
      hover: "hover:bg-emerald-600",
      text: "text-white",
      border: "border-emerald-600",
      ring: "ring-emerald-200",
      inactive:
        "border-emerald-500/40 hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-950/20",
    },
    P: {
      bg: "bg-blue-500",
      hover: "hover:bg-blue-600",
      text: "text-white",
      border: "border-blue-600",
      ring: "ring-blue-200",
      inactive:
        "border-blue-500/40 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/20",
    },
    F: {
      bg: "bg-red-500",
      hover: "hover:bg-red-600",
      text: "text-white",
      border: "border-red-600",
      ring: "ring-red-200",
      inactive:
        "border-red-500/40 hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-950/20",
    },
    W: {
      bg: "bg-amber-500",
      hover: "hover:bg-amber-600",
      text: "text-white",
      border: "border-amber-600",
      ring: "ring-amber-200",
      inactive:
        "border-amber-500/40 hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/20",
    },
  };

  return (
    <div className="mt-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        {/* Scores Section */}
        {!hideScores && (
          <div className="border-b border-gray-200 dark:border-gray-700 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-t-xl">
            <div className="px-5 py-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <StarIcon className="h-4 w-4 text-amber-500" />
                  Assessment Scores
                </h3>
                {scores.length > 0 && (
                  <button
                    onClick={() => setShowCandidateResult(true)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                  >
                    <DocumentChartBarIcon className="h-4 w-4" />
                    View Details
                  </button>
                )}
              </div>

              {scores.length !== 0 ? (
                <div className="space-y-2.5">
                  {scores.map((s) => (
                    <div
                      key={s.roleStatus.id}
                      className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors group"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="flex-shrink-0 w-14 h-8 bg-gradient-to-br from-amber-400 to-amber-500 rounded-lg flex items-center justify-center shadow-sm">
                          <span className="text-sm font-bold text-white">
                            {s.score}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p
                            className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words line-clamp-2"
                            title={s.roleStatus.name}
                          >
                            {s.roleStatus.name}
                          </p>
                          {takenWithoutSebFlags[s.roleStatus.id.toLowerCase()] && (
                            <span className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700">
                              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                              </svg>
                              Taken without SEB
                            </span>
                          )}
                        </div>
                      </div>
                      {s.roleAssessmentMappingId && (
                        <button
                          onClick={() =>
                            handleResetSpecificScore(
                              s.roleAssessmentMappingId!,
                              s.roleStatus.name
                            )
                          }
                          disabled={
                            resettingScoreId === s.roleAssessmentMappingId
                          }
                          className={`flex-shrink-0 p-2 rounded-lg transition-all ${
                            resettingScoreId === s.roleAssessmentMappingId
                              ? "bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                              : "text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100"
                          }`}
                          title="Reset this score"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full mb-3">
                    <StarIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                  </div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    No assessment scores yet
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Manual Evaluations Section */}
        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <CheckIcon className="h-4 w-4 text-blue-500" />
              Manual Evaluations
            </h3>
            <button
              type="button"
              onClick={interviewGuide.openManually}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-entntblue hover:underline"
            >
              <BookOpenIcon className="h-4 w-4" />
              Interview Guide
            </button>
          </div>

          <div className="space-y-3">
            {grades &&
              roleStatuses.slice(1, -2).map((rs, index) => {
                const isManualGradingType =
                  rs.roleStatusType?.name === "Interview" ||
                  rs.roleStatusType?.name === "Assignment";

                if (!isManualGradingType || !candidateReachedStatus(rs)) {
                  return null;
                }

                const hasScore =
                  manualScores[rs.id] && manualScores[rs.id] !== "";
                const currentScore = manualScores[rs.id] || "";

                return (
                  <div
                    key={index}
                    className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    {/* Role Name */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 mt-1">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        </div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-snug break-words">
                          {rs.name}
                        </h4>
                      </div>
                      <div className="flex items-center gap-2 border-gray-200 dark:border-gray-700">
                        {/* One merged affordance: before anything's submitted it's a plain
                            "Scorecard" button (fill it in); once there's a result, that
                            becomes the score/recommendation itself - clicking either one
                            opens the same detail view, which has its own Update action to
                            get back into the editable form. No separate button once a
                            score exists. */}
                        {structuredSummaries[rs.id] ? (
                          <div className="flex items-center gap-1">
                            <ScoreFormulaInfo
                              showCombinedRule={structuredSummaries[rs.id].submittedCount > 1}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setViewingStructured({ roleStatusId: rs.id, stageName: rs.name })
                              }
                              title="View the structured evaluation behind this grade"
                              className={`inline-flex flex-shrink-0 flex-col items-center leading-tight whitespace-nowrap px-2.5 py-1 rounded-lg text-xs font-semibold border transition-transform hover:scale-105 ${
                                structuredSummaries[rs.id].combinedRecommendation
                                  ? RECOMMENDATION_STYLES[
                                      structuredSummaries[rs.id].combinedRecommendation!
                                    ]
                                  : "bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600"
                              }`}
                            >
                              <span>
                                {structuredSummaries[rs.id].combinedRecommendation
                                  ? RECOMMENDATION_LABELS[
                                      structuredSummaries[rs.id].combinedRecommendation!
                                    ]
                                  : "Evaluation submitted"}
                              </span>
                              {structuredSummaries[rs.id].combinedNormalisedScore != null && (
                                <span className="text-[10px] font-normal opacity-75">
                                  {structuredSummaries[rs.id].combinedNormalisedScore}/100
                                </span>
                              )}
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              setScorecardModal({ roleStatusId: rs.id, stageName: rs.name })
                            }
                            title="Fill in the structured scorecard for this stage"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-entntblue border border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                          >
                            <DocumentChartBarIcon className="h-3.5 w-3.5" />
                            Scorecard
                          </button>
                        )}
                        <Popover className="relative flex-1">
                          <>
                            {hasScore ? (
                              <div className="flex items-center gap-2">
                                <Popover.Button className="inline-flex items-center py-1 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex-shrink-0">
                                  <div className="flex items-center gap-2 px-2 py-1 bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600">
                                    <StarIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
                                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                      {currentScore}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      / 10
                                    </span>
                                  </div>
                                </Popover.Button>
                              </div>
                            ) : (
                              <Popover.Button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors">
                                Add Score
                              </Popover.Button>
                            )}

                            <Popover.Panel className="absolute z-20 mt-2 right-0 bottom-10 w-64 max-w-[calc(100vw-2rem)] rounded-xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700">
                              {({ close }) => {
                                const inputValue =
                                  editingScoreValues[rs.id] !== undefined
                                    ? editingScoreValues[rs.id]
                                    : (manualScores[rs.id] ?? "");

                                return (
                                  <div className="p-4">
                                    <div className="flex items-center justify-between mb-4">
                                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                        {hasScore ? "Edit" : "Add"} {rs.name}{" "}
                                        Score
                                      </h4>
                                      <button
                                        onClick={() => close()}
                                        className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                      >
                                        <XMarkIcon className="h-4 w-4" />
                                      </button>
                                    </div>

                                    <div className="mb-4 flex items-center justify-between gap-3">
                                      <div className="flex flex-1 items-center min-w-0 gap-2">
                                        <input
                                          id={`score-input-${rs.id}`}
                                          type="number"
                                          min="0"
                                          max="10"
                                          step="0.1"
                                          placeholder="0.0"
                                          value={inputValue}
                                          onChange={(e) =>
                                            handleScoreInputChange(
                                              rs.id,
                                              e.target.value
                                            )
                                          }
                                          className="flex-1 w-full min-w-0 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent [-moz-appearance:_textfield] [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none"
                                          autoFocus
                                        />
                                        <span className="text-sm font-medium text-gray-500 whitespace-nowrap">
                                          / 10
                                        </span>
                                      </div>
                                      <button
                                        type="button"
                                        className="px-3 py-2 text-sm font-medium text-white bg-entntblue rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                                        onClick={() => {
                                          handleSaveScore(rs);
                                          close();
                                        }}
                                      >
                                        Save
                                      </button>
                                    </div>
                                  </div>
                                );
                              }}
                            </Popover.Panel>
                          </>
                        </Popover>
                      </div>
                    </div>

                    {/* Grade Buttons */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(["E", "P", "F", "W"] as const).map((gradeOption) => {
                        const isSelected = grades[index] === gradeOption;
                        const colors = gradeColors[gradeOption];

                        return (
                          <button
                            key={gradeOption}
                            onClick={() =>
                              handleGradeClick(index, gradeOption, rs)
                            }
                            className={`
                              px-2 py-1 rounded-lg text-xs font-semibold transition-all duration-200 border-2
                              ${
                                isSelected
                                  ? `${colors.bg} ${colors.text} shadow-md ring-2 ${colors.ring} scale-105`
                                  : `bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${colors.inactive} hover:scale-105 hover:shadow-md`
                              }
                            `}
                          >
                            {gradeOption === "E"
                              ? "Excellent"
                              : gradeOption === "P"
                                ? "Pass"
                                : gradeOption === "F"
                                  ? "Fail"
                                  : "Waitlist"}
                          </button>
                        );
                      })}
                    </div>

                    {/* Score Section */}
                  </div>
                );
              })}
          </div>

          {roleStatuses
            .slice(1, -2)
            .filter(
              (rs) =>
                (rs.roleStatusType?.name === "Interview" ||
                  rs.roleStatusType?.name === "Assignment") &&
                candidateReachedStatus(rs)
            ).length === 0 && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full mb-3">
                <CheckIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
              </div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                No manual evaluations yet
              </p>
            </div>
          )}
        </div>
      </div>

      {roleId && candidateId && showCandidateResult && (
        <CandidateAssessmentResult
          roleId={roleId}
          candidateId={candidateId}
          onClose={() => {
            setShowCandidateResult(false);
          }}
        />
      )}

      <ConfirmModal
        type="danger"
        open={showConfirmModal}
        setOpen={setShowConfirmModal}
        onConfirm={confirmResetScore}
        onCancel={() => setModalData(null)}
        title="Reset Score"
        confirmButtonTitle="Reset"
        description={
          modalData
            ? `Are you sure you want to reset the score for "${modalData.statusName}"? This action cannot be undone.`
            : ""
        }
      />

      <ConfirmModal
        type="info"
        open={showSuccessModal}
        setOpen={setShowSuccessModal}
        onConfirm={() => setShowSuccessModal(false)}
        title="Success"
        confirmButtonTitle="OK"
        description={
          modalData
            ? `Score for "${modalData.statusName}" has been reset successfully!`
            : "Score has been reset successfully!"
        }
      />

      {scorecardModal && candidateId && roleId && (
        <RequiredScorecardModal
          candidateId={candidateId}
          roleId={roleId}
          roleStatusId={scorecardModal.roleStatusId}
          stageName={scorecardModal.stageName}
          candidateName={
            candidate ? `${candidate.firstName} ${candidate.lastName}` : undefined
          }
          attemptedRating={scorecardModal.rating}
          allowSkip={scorecardModal.allowSkip}
          onSkip={() => {
            scorecardModal.applyPendingGrade?.();
            setScorecardModal(null);
          }}
          onClose={() => setScorecardModal(null)}
          onSubmitted={() => {
            // Fire the pending grade first so it overwrites whatever
            // ManualEvaluationProjectionService just derived from the scorecard's
            // scores - the interviewer's click, not the computed band, wins.
            scorecardModal.applyPendingGrade?.();
            refreshManualGrades();
            refreshStructuredSummaries();
          }}
        />
      )}

      {viewingStructured && candidateId && roleId && (
        <StructuredEvaluationDetailModal
          candidateId={candidateId}
          roleId={roleId}
          roleStatusId={viewingStructured.roleStatusId}
          stageName={viewingStructured.stageName}
          candidateName={
            candidate ? `${candidate.firstName} ${candidate.lastName}` : undefined
          }
          onClose={() => setViewingStructured(null)}
          onSubmitted={() => {
            refreshManualGrades();
            refreshStructuredSummaries();
          }}
        />
      )}

      {interviewGuide.open && <InterviewGuideModal onClose={interviewGuide.close} />}
    </div>
  );
}
